import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpResponse,
} from '@angular/common/http';
import { environment } from '../../environments/environment';
import { firstValueFrom, fromEvent, Observable, takeUntil } from 'rxjs';

export interface UploadSuccessResponse {
  key: string;
  uploadId: string;
  bucketName: string;
}

export interface InitUploadSuccessResponse {
  Key: string;
  Bucket: string;
  UploadId: string;
  Region?: string;
}

export interface PartUrl {
  partNumber: number;
  url: string;
}

export interface PartUrlsResponse {
  urls: PartUrl[];
}

export interface PartUrlsRequest {
  aiScribeId: string;
  uploadId: string;
  fileExtension: string;
  partNumbers: number[];
}

@Injectable({
  providedIn: 'root',
})
export class FileUploadService {
  constructor(private readonly httpService: HttpClient) {}

  init(fileExtension: string, aiScribeId: string, sessionId: string) {
    return this.httpService.post<InitUploadSuccessResponse>(
      environment.api_host +
        `sessions/${sessionId}/ai-scribe/initiate-multipart-upload`,
      {
        fileExtension,
        aiScribeId,
        sessionId,
      },
    );
  }

  /**
   * Presigned S3 UploadPart URLs.
   *
   * `partNumbers` may hold a single element: the live recording paths presign
   * one part at a time because they do not know the part count in advance.
   */
  partUrls(
    sessionId: string,
    body: PartUrlsRequest,
  ): Observable<PartUrlsResponse> {
    return this.httpService.post<PartUrlsResponse>(
      environment.api_host + `sessions/${sessionId}/ai-scribe/upload-part-urls`,
      body,
    );
  }

  /**
   * PUT one part straight to S3 against a presigned URL.
   *
   * `observe: 'response'` is what makes the `ETag` readable: on a direct PUT it
   * is a response header rather than a body field. It is only readable at all
   * if the bucket CORS policy carries `ExposeHeaders: ["ETag"]`.
   */
  putPart(url: string, body: Blob): Observable<HttpResponse<string>> {
    return this.httpService.put(url, body, {
      observe: 'response',
      // S3 answers an UploadPart with an empty body and errors with XML; asking
      // for text keeps a JSON parse failure from masking the real status code.
      responseType: 'text',
    });
  }

  // Attempts per part, including the first.
  private readonly uploadPartAttempts = 3;

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * PUT one part to S3, retrying a transient failure, and return its ETag.
   *
   * A 403 means the presigned URL expired rather than that the user lost
   * access, so it triggers one re-presign instead of a plain retry. The token is
   * never involved: the PUT carries no auth header.
   *
   * Shared by every direct-to-S3 path (the uploaded-file submit and the
   * recording backup). `signal` cancels the request in flight, HttpClient
   * cancels by unsubscribing, and stops any further attempt.
   */
  async putPartWithRetry(
    url: string,
    blob: Blob,
    partNumber: number,
    onUrlExpired: () => Promise<string | undefined>,
    signal?: AbortSignal,
  ): Promise<string> {
    let currentUrl = url;
    let hasRepresigned = false;
    let lastError: unknown;

    for (let attempt = 1; attempt <= this.uploadPartAttempts; attempt++) {
      throwIfAborted(signal);
      try {
        const put$ = signal
          ? this.putPart(currentUrl, blob).pipe(
              takeUntil(fromEvent(signal, 'abort')),
            )
          : this.putPart(currentUrl, blob);
        const response = await firstValueFrom(put$, { defaultValue: null });
        throwIfAborted(signal);
        const etag = response?.headers.get('ETag');
        if (!etag) {
          // The PUT succeeded but the header is unreadable, which is what a
          // bucket CORS policy missing ExposeHeaders: ["ETag"] looks like from
          // here. Completing with a short parts list would corrupt the object,
          // so this is a failure rather than something to skip.
          throw new Error(
            `Part ${partNumber} uploaded but no ETag header was readable`,
          );
        }
        // Returned verbatim, quotes included: S3 compares these strings when
        // completing the upload.
        return etag;
      } catch (err) {
        if (isAbortError(err)) throw err;
        lastError = err;
        console.error(
          `Part ${partNumber} upload attempt ${attempt} failed`,
          err,
        );

        if (
          err instanceof HttpErrorResponse &&
          err.status === 403 &&
          !hasRepresigned
        ) {
          hasRepresigned = true;
          const refreshed = await onUrlExpired();
          if (refreshed) {
            currentUrl = refreshed;
            continue;
          }
        }

        if (attempt < this.uploadPartAttempts) {
          const backoff = 1000 * Math.pow(2, attempt - 1);
          await this.delay(backoff + Math.random() * 250);
        }
      }
    }

    // HttpErrorResponse is not an Error subclass, but its status is what lets
    // a caller tell "offline" (0) from a server failure, so it is kept as is.
    throw lastError instanceof Error || lastError instanceof HttpErrorResponse
      ? lastError
      : new Error(`Failed to upload part ${partNumber}`);
  }

  /**
   * Proxy part upload. Superseded by `partUrls` + `putPart` and no longer
   * called.
   */
  upload_part(formData: FormData, sessionId: string): Observable<any> {
    return this.httpService.post<any>(
      environment.api_host +
        `sessions/${sessionId}/ai-scribe/upload-part-session-recording`,
      formData,
    );
  }

  complete_upload(
    key: any,
    uploadId: any,
    title: any,
    audio_language: any,
    parts: { ETag: string; PartNumber: number }[],
    sessionId: string,
  ): Observable<UploadSuccessResponse> {
    return this.httpService.post<UploadSuccessResponse>(
      environment.api_host +
        `sessions/${sessionId}/ai-scribe/complete-session-recording`,
      {
        key,
        uploadId,
        title: title,
        language_code: audio_language,
        parts,
      },
    );
  }
}

/** Thrown when an AbortSignal cancels an upload; never retried. */
export function abortError(): Error {
  const error = new Error('Upload cancelled');
  error.name = 'AbortError';
  return error;
}

export function isAbortError(err: unknown): boolean {
  return err instanceof Error && err.name === 'AbortError';
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw abortError();
}
